import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  shareReplay,
  startWith,
  Subject,
  tap,
} from 'rxjs';

import {
  canUseForceOrderingBypass,
  parseForceOrderingQueryParam,
  readForceOrderingQueryFromUrl,
  resolveForceOrderingAvailable,
} from './force-ordering-available.util';

/**
 * Dev-only bypass for kiosk ordering-available checks via `?forceOrdering=1` or the kiosk closed modal.
 * Sticky for the SPA session until `forceOrdering=0` or full reload without the param.
 */
@Injectable({ providedIn: 'root' })
export class ForceOrderingAvailableService {
  private readonly router = inject(Router);
  private urlSticky = false;
  private devSessionBypass = false;
  private loggedActive = false;
  private readonly refresh$ = new Subject<void>();

  readonly isForceOrderingActive$: Observable<boolean> = merge(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    this.refresh$.pipe(map(() => null as string | null)),
  ).pipe(
    startWith(null as string | null),
    map(navigationUrl => this.computeActive(navigationUrl)),
    distinctUntilChanged(),
    tap(active => this.logIfActive(active)),
    shareReplay(1),
  );

  constructor() {
    this.isForceOrderingActive$.subscribe();
  }

  /** Enables bypass for the current dev session (localhost / webapp-dev preview only). */
  enableDevSessionBypass(): void {
    if (!canUseForceOrderingBypass()) {
      return;
    }
    this.devSessionBypass = true;
    this.refresh$.next();
  }

  private computeActive(navigationUrl: string | null): boolean {
    if (!canUseForceOrderingBypass()) {
      this.urlSticky = false;
      this.devSessionBypass = false;
      return false;
    }

    const resolvedUrl =
      navigationUrl ?? (typeof window !== 'undefined' ? window.location.href : this.router.url);
    const queryValue = readForceOrderingQueryFromUrl(resolvedUrl);
    const parsed = parseForceOrderingQueryParam(queryValue);

    if (parsed === false) {
      this.devSessionBypass = false;
      this.urlSticky = false;
      return false;
    }

    this.urlSticky = resolveForceOrderingAvailable(queryValue, this.urlSticky);
    if (parsed === true) {
      this.devSessionBypass = false;
    }

    return this.urlSticky || this.devSessionBypass;
  }

  private logIfActive(active: boolean): void {
    if (active && !this.loggedActive) {
      this.loggedActive = true;
      console.info(
        '[Dood] forceOrdering is active: marketplace.ordering_available is forced to true (dev only).',
      );
    }
    if (!active) {
      this.loggedActive = false;
    }
  }
}
